import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeOff, Building2, ArrowLeft } from 'lucide-react';
import '../styles/osagest-theme.css';
import { soloFicha } from '@/lib/fichajes';

function LoginOSAGEST({ setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: Email, 2: Password
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);

  // El email se guarda en minúsculas y sin espacios. Si lo enviamos tal cual
  // se escribió, un espacio del autocompletar o una mayúscula bastan para que
  // el servidor diga que la cuenta no existe.
  const emailNormalizado = email.trim().toLowerCase();

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!emailNormalizado) {
      toast.error('Por favor introduce tu email');
      return;
    }

    setLoading(true);
    try {
      // Buscar a qué empresa pertenece este email
      const response = await axiosInstance.post('/auth/check-email', { email: emailNormalizado });

      if (response.data.exists) {
        setBusinessInfo(response.data.business);
        setStep(2);
      } else {
        toast.error('No encontramos una cuenta con ese email');
      }
    } catch (error) {
      // Distinguir "esa cuenta no existe" de "no he podido preguntarlo" es lo
      // que evita que alguien se pase media hora dudando de su propio email
      // cuando lo que falla es el servidor o el límite de intentos.
      console.error('Error verificando email:', error);
      const status = error.response?.status;
      if (status === 429) {
        toast.error('Demasiados intentos seguidos. Espera un minuto y vuelve a probar.');
      } else if (!error.response) {
        toast.error('No hemos podido conectar con el servidor. Revisa tu conexión.');
      } else {
        toast.error(
          error.response?.data?.detail ||
          'No hemos podido comprobar el email. Inténtalo de nuevo en unos segundos.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login', {
        email: emailNormalizado,
        password
      });
      
      localStorage.setItem('token', response.data.access_token);
      setUser(response.data.user);

      // Contraseña puesta por un administrador: hay que elegir una propia
      // antes de poder usar la aplicación.
      if (response.data.user?.must_change_password) {
        localStorage.setItem('must_change_password', '1');
        toast.info('Elige una contraseña propia para continuar');
        navigate('/cambiar-password');
        return;
      }

      localStorage.removeItem('must_change_password');
      toast.success('Inicio de sesión exitoso');
      // Quien solo ficha entra directo a fichar: no tiene panel.
      navigate(soloFicha(response.data.user) ? '/fichar' : '/dashboard');
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        toast.error('Demasiados intentos seguidos. Espera un minuto y vuelve a probar.');
      } else if (!error.response) {
        toast.error('No hemos podido conectar con el servidor. Revisa tu conexión.');
      } else if (status === 401) {
        toast.error('Contraseña incorrecta');
      } else {
        toast.error(error.response?.data?.detail || 'No hemos podido iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setPassword('');
    setBusinessInfo(null);
  };

  return (
    <div className="min-h-screen flex" style={{backgroundColor: '#F8F9FA'}}>
      {/* Columna Izquierda - Branding */}
      <div className="hidden lg:flex lg:w-1/2 osagest-bg-primary items-center justify-center p-12">
        <div className="max-w-md text-center">
          <img 
            src="/logos/osagest-logo.png" 
            alt="OSAGEST" 
            className="h-16 w-auto mx-auto mb-8"
          />
          <h2 className="osagest-h1 mb-4" style={{color: 'var(--osagest-white)'}}>
            Plataforma de Gestión Integral
          </h2>
          <p className="osagest-text-body" style={{color: 'var(--osagest-white)', opacity: 0.9}}>
            Para Autónomos y Pymes Españolas
          </p>
        </div>
      </div>

      {/* Columna Derecha - Formulario Login */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo móvil */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logos/osagest-isotipo.png" 
              alt="OSAGEST" 
              className="h-12 w-auto mx-auto mb-4"
            />
          </div>

          <div className="osagest-card" style={{padding: '32px'}}>
            {/* Paso 1: Email */}
            {step === 1 && (
              <>
                <div className="text-center mb-6">
                  <h1 className="osagest-h2 mb-2">Iniciar Sesión</h1>
                  <p className="osagest-text-description">
                    Introduce tu email para continuar
                  </p>
                </div>

                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label className="osagest-text-label block mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="osagest-input w-full"
                      required
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="osagest-btn osagest-btn-primary w-full"
                  >
                    {loading ? 'Verificando...' : 'Continuar'}
                    <ArrowRight className="ml-2 h-4 w-4 inline" />
                  </button>
                </form>

                <div className="mt-6 pt-6 border-t" style={{borderColor: 'var(--osagest-border-color)'}}>
                  <p className="osagest-text-small text-center" style={{color: 'var(--osagest-gray-medium)'}}>
                    ¿Tu empresa no está registrada?{' '}
                    <button
                      onClick={() => navigate('/registro')}
                      className="font-semibold"
                      style={{color: 'var(--osagest-primary)'}}
                    >
                      Regístrate aquí
                    </button>
                  </p>
                </div>
              </>
            )}

            {/* Paso 2: Password */}
            {step === 2 && businessInfo && (
              <>
                <button
                  onClick={handleBack}
                  className="flex items-center mb-4 osagest-text-label"
                  style={{color: 'var(--osagest-gray-medium)'}}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Cambiar email
                </button>

                <div className="mb-6">
                  <div className="osagest-card" style={{
                    backgroundColor: 'rgba(51, 102, 204, 0.05)',
                    border: '1px solid rgba(51, 102, 204, 0.2)',
                    padding: '16px',
                    marginBottom: '24px'
                  }}>
                    <div className="flex items-center">
                      <Building2 className="h-5 w-5 mr-3" style={{color: 'var(--osagest-primary)'}} />
                      <div>
                        <p className="osagest-text-label" style={{color: 'var(--osagest-gray-medium)'}}>
                          Iniciando sesión en
                        </p>
                        <p className="osagest-text-body font-semibold">
                          {businessInfo.business_name}
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="osagest-text-description mb-1">
                    {email}
                  </p>
                </div>

                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <label className="osagest-text-label block mb-2">
                      Contraseña
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Introduce tu contraseña"
                        className="osagest-input w-full pr-10"
                        required
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        style={{color: 'var(--osagest-gray-medium)'}}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="osagest-btn osagest-btn-primary w-full"
                  >
                    {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/')}
              className="osagest-text-small"
              style={{color: 'var(--osagest-gray-medium)'}}
            >
              ← Volver al inicio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginOSAGEST;
